import { useState } from 'react';
import { api } from '../lib/api.js';

/**
 * Download the stored meeting video. The click checks that the file is ready,
 * then navigates to the authenticated route, which redirects to a signed download.
 */
export function DownloadVideoButton({
  meetingId,
  available,
  preparing = false,
  compact = false,
}: {
  meetingId: string;
  /** Durable cloud copy exists. */
  available: boolean;
  /** Cloud copy is still uploading. */
  preparing?: boolean;
  compact?: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onClick(): Promise<void> {
    if (!available || preparing || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const status = await api.videoStatus(meetingId);
      if (status.status !== 'ready') {
        setErr(status.message || 'This video is not available to download.');
        return;
      }
      window.location.assign(`/api/meetings/${meetingId}/video`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not download this recording.');
    } finally {
      setBusy(false);
    }
  }

  const label = busy ? 'Preparing download…' : preparing ? 'Saving video…' : 'Download video';
  const title = preparing
    ? 'The recording is still being saved to the cloud.'
    : available
      ? 'Download the video file for this meeting.'
      : 'Save this recording to the cloud before downloading.';

  return (
    <span className="video-download-wrap">
      <button
        type="button"
        className={`btn btn--soft${compact ? ' btn--sm' : ''}`}
        onClick={() => void onClick()}
        disabled={busy || preparing || !available}
        aria-busy={busy || preparing}
        title={title}
      >
        {label}
      </button>
      {err && <span className="video-download__error" role="alert">{err}</span>}
    </span>
  );
}
